import { slugName } from '@lapo/core';
import { displayTitle, type LocalNote } from './noteIndex';

/**
 * Local, offline name → noteId resolver — the client half of clickthrough. Clicking a `[[Foo]]`
 * wikilink needs to open Foo's page, but links are stored by NAME while the app navigates by id. The
 * server keeps this mapping (an entity's `noteId`, plus `aliasOf` pointers), but reaching it is a
 * round-trip; this mirrors a slim version of it purely from what the device already knows:
 *
 *  • the note catalog (every page title this device has seen), keyed by {@link slugName} so
 *    `[[vickers]]` matches a page titled "Vickers";
 *  • per-note alias lists (a page's `aliases` doc attribute), so `[[alias]]` folds into its page.
 *
 * A miss means "no such page yet" — the caller creates it (Logseq/Roam "red link" behaviour). Kept
 * as a plain class fed from React refs so the click handler can read the freshest mapping without a
 * stale closure.
 */
export class LinkResolver {
  private titleToId = new Map<string, string>();
  private aliasToId = new Map<string, string>();

  /** Rebuild the title map from the merged note catalog. Called whenever the catalog changes. */
  setNotes(notes: readonly LocalNote[]): void {
    const map = new Map<string, string>();
    for (const n of notes) {
      // Raw title first, then the display title, so the display form wins on conflict (e.g. journals
      // read as their date label). 'Untitled'/empty never becomes a link target.
      for (const name of [n.title, displayTitle(n)]) {
        const key = slugName(name);
        if (key && key !== 'untitled') map.set(key, n.noteId);
      }
    }
    this.titleToId = map;
  }

  /** Record a page's alias names so `[[alias]]` resolves to it. Additive; replaces this note's set. */
  setAliases(noteId: string, aliases: readonly string[]): void {
    // Drop any prior aliases pointing at this note, then re-add — keeps the map correct when a page's
    // alias list is edited down.
    for (const [key, id] of this.aliasToId) if (id === noteId) this.aliasToId.delete(key);
    for (const alias of aliases) {
      const key = slugName(alias);
      if (key) this.aliasToId.set(key, noteId);
    }
  }

  /** noteId for a link name, or null when no page (yet) carries that title/alias. */
  resolve(name: string): string | null {
    const key = slugName(name);
    if (!key) return null;
    return this.titleToId.get(key) ?? this.aliasToId.get(key) ?? null;
  }
}
